import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

export interface DynamicPage {
  id: string;
  title: string;
  path: string;
  contentHtml: string;
}

export interface DynamicSection {
  id: string;
  location: string; // e.g., 'home-bottom', 'article-sidebar', 'header-ad'
  contentHtml: string;
}

interface SiteContextType {
  pages: DynamicPage[];
  sections: DynamicSection[];
  addPage: (page: DynamicPage) => void;
  addSection: (section: DynamicSection) => void;
  removePage: (id: string) => void;
  removeSection: (id: string) => void;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pages, setPages] = useState<DynamicPage[]>([]);
  const [sections, setSections] = useState<DynamicSection[]>([]);

  useEffect(() => {
    const storedPages = localStorage.getItem('literafan_dynamic_pages');
    const storedSections = localStorage.getItem('literafan_dynamic_sections');
    
    if (storedPages) setPages(JSON.parse(storedPages));
    if (storedSections) setSections(JSON.parse(storedSections));
  }, []);

  const addPage = (page: DynamicPage) => {
    const newPages = [...pages, page];
    setPages(newPages);
    localStorage.setItem('literafan_dynamic_pages', JSON.stringify(newPages));
  };

  const addSection = (section: DynamicSection) => {
    const newSections = [...sections, section];
    setSections(newSections);
    localStorage.setItem('literafan_dynamic_sections', JSON.stringify(newSections));
  };

  const removePage = (id: string) => {
    const newPages = pages.filter(p => p.id !== id);
    setPages(newPages);
    localStorage.setItem('literafan_dynamic_pages', JSON.stringify(newPages));
  };

  const removeSection = (id: string) => {
    const newSections = sections.filter(s => s.id !== id);
    setSections(newSections);
    localStorage.setItem('literafan_dynamic_sections', JSON.stringify(newSections));
  };

  return (
    <SiteContext.Provider value={{ pages, sections, addPage, addSection, removePage, removeSection }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};
